import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import ReceiptUploader from './ReceiptUploader';

const ExpenseDialog = ({
  open,
  onOpenChange,
  expense,
  setExpense,
  projects,
  employees,
  currentEmployeeId,
  isAdminOrManager,
  expenseCategories,
  currencies,
  paymentMethods = [],
  receiptFiles,
  setReceiptFiles,
  existingReceipts = [],
  onDeleteReceipt,
  onViewReceipt,
  onSubmit,
  onCancel,
  assets = []
}) => {
  const { t } = useTranslation();
  // Get team members for selected project (or all employees for non-project)
  const projectEmployees = useMemo(() => {
    if (!expense.project_id || !employees) return [];
    
    // For non-project expenses, show all employees
    if (expense.project_id === 'non-project') {
      return employees;
    }
    
    const project = projects.find(p => p.id === expense.project_id);
    if (!project?.team_members) return [];
    
    const teamMemberIds = project.team_members.map(tm => tm.employee_id);
    return employees.filter(e => teamMemberIds.includes(e.id));
  }, [expense.project_id, projects, employees]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('expenses.dialog.add')}</DialogTitle>
          <DialogDescription>
            {t('expenses.dialog.addDesc')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
          <div className="sm:col-span-2">
            <Label>{t('expenses.fields.project')}</Label>
            <Select 
              value={expense.project_id} 
              onValueChange={(v) => setExpense(prev => ({ ...prev, project_id: v, assigned_employee_id: currentEmployeeId || '' }))}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('expenses.ph.selectProject')} />
              </SelectTrigger>
              <SelectContent>
                {/* Non-project option at the top */}
                <SelectItem value="non-project" className="font-semibold text-gray-600 border-b">
                  📋 Non-project (General Expense)
                </SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Employee Assignment */}
          <div className="sm:col-span-2">
            <Label>{t('expenses.fields.employee')}</Label>
            <Select 
              value={expense.assigned_employee_id || currentEmployeeId || ''} 
              onValueChange={(v) => setExpense(prev => ({ ...prev, assigned_employee_id: v }))}
              disabled={!expense.project_id}
            >
              <SelectTrigger>
                <SelectValue placeholder={expense.project_id ? t('expenses.ph.selectEmployee') : t('expenses.ph.selectProjectFirst')} />
              </SelectTrigger>
              <SelectContent>
                {/* For non-project expenses, show all employees */}
                {expense.project_id === 'non-project' ? (
                  employees?.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} {emp.id === currentEmployeeId ? '(Me)' : ''}
                    </SelectItem>
                  ))
                ) : projectEmployees.length > 0 ? (
                  projectEmployees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} {emp.id === currentEmployeeId ? '(Me)' : ''}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value={currentEmployeeId || 'self'}>
                    {employees?.find(e => e.id === currentEmployeeId)?.name || 'Myself'}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {isAdminOrManager 
                ? t('expenses.fields.employeeHint')
                : t('expenses.fields.employeeSelfHint')}
            </p>
          </div>
          
          <div>
            <Label>{t('expenses.fields.category')}</Label>
            <Select 
              value={expense.category} 
              onValueChange={(v) => setExpense(prev => ({ ...prev, category: v, subcategory: '' }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(expenseCategories)
                  .filter(([key, cat]) => {
                    // Handle both old format (array) and new format (object with visible)
                    if (Array.isArray(cat)) return true;
                    return cat.visible !== false;
                  })
                  .map(([key, cat]) => {
                    // Get display name from new format or capitalize key for old format
                    const displayName = Array.isArray(cat) 
                      ? key.charAt(0).toUpperCase() + key.slice(1) 
                      : (cat.display_name || key);
                    return (
                      <SelectItem key={key} value={key}>{displayName}</SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>{t('expenses.fields.subcategory')}</Label>
            <Select 
              value={expense.subcategory} 
              onValueChange={(v) => setExpense(prev => ({ ...prev, subcategory: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('expenses.ph.selectSubcategory')} />
              </SelectTrigger>
              <SelectContent>
                {(() => {
                  const cat = expenseCategories[expense.category];
                  // Handle both old format (array) and new format (object with subcategories)
                  const subcats = Array.isArray(cat) ? cat : (cat?.subcategories || []);
                  return subcats.map(sub => (
                    <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>{t('expenses.fields.amount')}</Label>
            <Input
              type="number"
              step="0.01"
              value={expense.amount}
              onChange={(e) => setExpense(prev => ({ ...prev, amount: e.target.value }))}
              placeholder={t('expenses.ph.amount')}
            />
          </div>
          
          <div>
            <Label>{t('expenses.fields.currency')}</Label>
            <Select 
              value={expense.currency} 
              onValueChange={(v) => setExpense(prev => ({ ...prev, currency: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>{t('expenses.fields.date')}</Label>
            <Input
              type="date"
              value={expense.expense_date}
              onChange={(e) => setExpense(prev => ({ ...prev, expense_date: e.target.value }))}
            />
          </div>
          
          <div>
            <Label>{t('expenses.fields.vendor')}</Label>
            <Input
              value={expense.vendor}
              onChange={(e) => setExpense(prev => ({ ...prev, vendor: e.target.value }))}
              placeholder={t('expenses.ph.vendor')}
            />
          </div>
          
          <div>
            <Label>{t('expenses.fields.invoiceNumber')}</Label>
            <Input
              value={expense.invoice_number}
              onChange={(e) => setExpense(prev => ({ ...prev, invoice_number: e.target.value }))}
              placeholder={t('expenses.ph.invoice')}
            />
          </div>
          
          {/* Payment Method */}
          <div>
            <Label>{t('expenses.fields.paymentMethod')}</Label>
            <Select 
              value={expense.payment_method || ''} 
              onValueChange={(v) => setExpense(prev => ({ ...prev, payment_method: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('expenses.ph.selectPaymentMethod')} />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map(method => (
                  <SelectItem key={method} value={method}>{method}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Receipts section - now supports multiple with camera/gallery */}
          <div className="sm:col-span-2">
            <ReceiptUploader
              receipts={existingReceipts}
              pendingFiles={receiptFiles || []}
              onFilesChange={setReceiptFiles}
              onDeleteReceipt={onDeleteReceipt}
              onViewReceipt={onViewReceipt}
              maxReceipts={5}
            />
          </div>
          
          <div className="sm:col-span-2">
            <Label>{t('expenses.fields.description')}</Label>
            <Textarea
              value={expense.description}
              onChange={(e) => setExpense(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('expenses.ph.description')}
              rows={3}
            />
          </div>

          {/* Link to Inventory Asset */}
          {assets.length > 0 && (
            <div className="sm:col-span-2">
              <Label>{t('expenses.fields.linkAsset')}</Label>
              <Select
                value={expense.asset_id || 'none'}
                onValueChange={(v) => setExpense(prev => ({ ...prev, asset_id: v === 'none' ? '' : v }))}
              >
                <SelectTrigger data-testid="expense-asset-select">
                  <SelectValue placeholder={t('expenses.fields.noAssetLinked')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('expenses.fields.noAssetLinked')}</SelectItem>
                  {assets.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.inventory_number} — {a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">{t('expenses.fields.linkAssetHint')}</p>
            </div>
          )}
          
          {/* Billable to Customer */}
          {expense.project_id && expense.project_id !== 'non-project' && (
            <div className="sm:col-span-2 flex items-center space-x-2 p-3 border rounded-lg bg-blue-50/50">
              <Checkbox
                id="billable_to_customer"
                checked={expense.billable_to_customer || false}
                onCheckedChange={(checked) => setExpense(prev => ({ ...prev, billable_to_customer: checked }))}
                data-testid="billable-to-customer-checkbox"
              />
              <Label htmlFor="billable_to_customer" className="text-sm cursor-pointer">
                Billable to customer — include on next invoice draft
              </Label>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="dialog-cancel-btn">
            {t('common.cancel')}
          </Button>
          <Button onClick={onSubmit} data-testid="dialog-create-expense-btn">
            {t('expenses.actions.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseDialog;
